import {isDefined} from 'Application/utils/typeGuards';

import type {LexicalContent} from '@nfq/payload-lexical-parser';
import type {TextImageModuleIntro} from 'types/payload-types';

/**
 * A custom hook that extracts and normalizes content for the `IntoHeader` component.
 * This hook processes the optional `intro` object and returns its `headline`, `overline`,
 * and `text` properties in a consistent format. If the `intro` is not defined, the returned
 * values remain `undefined`. This allows consuming components to safely check for the presence
 * of header content before rendering.
 *
 * @param intro The intro content object, which may include a `headline`, `overline`, and `text`. If omitted or `null`, no values will be returned.
 * @returns An object containing the extracted `headline`, `overline`, and `text` values.
 *
 * @example
 * ```tsx
 * const { headline, overline, text } = useIntroHeader(introData);
 *
 * return (
 *   <>
 *     {overline && <span>{overline}</span>}
 *     {headline && <h2>{headline}</h2>}
 *     {text && <PayloadLexical description={text} />}
 *   </>
 * );
 * ```
 */
export const useIntroHeader = (intro?: TextImageModuleIntro | null) => {
    let headline: string | null | undefined;
    let overline: string | null | undefined;
    let text: LexicalContent | null | undefined;

    if (isDefined(intro)) {
        headline = intro.headline;
        overline = intro.overline;
        text = intro.text;
    }

    return {
        headline,
        overline,
        text
    };
};